import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === currentCategory?.name);

  const categoryName = currentCategory?.name || '未知分类';
  const categoryDesc = currentCategory?.description || '';

  const mainArticles = filteredArticles;
  const otherCategories = categories.filter((c) => c.slug !== slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 51吃瓜网站`,
    description: categoryDesc,
    url: `https://www.51chigua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51吃瓜网站',
      url: 'https://www.51chigua.com',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.51chigua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: categoryName }]} />

        <section className="py-8 md:py-12 px-4 md:px-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-900">
                {categoryName}
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                {categoryDesc}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8 pb-6 border-b border-background-200">
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-accent-100/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-primary-900'
                      : 'bg-background-100 text-foreground-700 hover:bg-accent-100/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {mainArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {mainArticles.map((article, idx) => {
                  const isMiddleCard = idx % 3 === 1;
                  return (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group cursor-pointer"
                    >
                      <article
                        className={`rounded-2xl overflow-hidden transition-transform duration-300 group-hover:scale-[1.02] ${
                          isMiddleCard ? 'bg-primary-500' : 'bg-background-100'
                        }`}
                      >
                        <div className="aspect-[3/2] overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            title={article.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4 md:p-5">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 whitespace-nowrap ${
                              isMiddleCard
                                ? 'bg-accent-500 text-primary-900'
                                : 'bg-accent-100 text-accent-700'
                            }`}
                          >
                            {article.category}
                          </span>
                          <h2
                            className={`text-base md:text-lg font-bold leading-snug line-clamp-3 ${
                              isMiddleCard ? 'text-background-50' : 'text-foreground-900'
                            }`}
                          >
                            {article.title}
                          </h2>
                          <div className={`flex items-center gap-3 mt-3 text-xs ${isMiddleCard ? 'text-background-50/60' : 'text-foreground-500'}`}>
                            <span>{article.date}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.views >= 10000
                                ? `${(article.views / 10000).toFixed(1)}万`
                                : article.views}
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}

            {otherCategories.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">浏览其他分类</h3>
                <div className="flex flex-wrap gap-2">
                  {otherCategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="px-4 py-2 rounded-full bg-background-100 text-sm font-medium text-foreground-700 hover:bg-accent-100/60 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}